import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Activity, Film, ListVideo, ShieldCheck, Video, X, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createSession,
  getExampleVideoFrame,
  initDirectUpload,
} from '../../api/beachvision';
import { setExampleVideoPending, setPendingDirectUpload } from '../../utils/sessionDirectUpload';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { LazyFrameImage } from '../../components/beachvision/LazyFrameImage';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';

const MAX_BYTES = 500 * 1024 * 1024;
const ACCEPT = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/avi': ['.avi'],
};
const EXAMPLE_VIDEOS = ['480-trim.mp4', '480-trim-2.mp4', '480-trim-3.mov', 'moguls.mp4', 'trimmed.mp4'];

function filenameWithoutExtension(name) {
  if (!name) return '';
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(0, i) : name;
}

function formatFileSizeMb(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Upload flow with coral dashed dropzone, optional file summary, and feature highlights.
 */
function UploadInner() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [title, setTitle] = useState('');
  const [recordedDate, setRecordedDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [uploading, setUploading] = useState(false);
  const [loadingExample, setLoadingExample] = useState(false);
  const [isExampleModalOpen, setIsExampleModalOpen] = useState(false);

  useEffect(() => {
    if (!isExampleModalOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsExampleModalOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isExampleModalOpen]);

  const onDrop = useCallback((accepted, rejected) => {
    setFileError('');
    if (rejected?.length) {
      setFileError('Please choose an MP4, MOV, or AVI file.');
      return;
    }
    const f = accepted[0];
    if (f && f.size > MAX_BYTES) {
      setFileError('File is too large. Maximum size is 500MB.');
      setFile(null);
      return;
    }
    if (f) {
      setFile(f);
      setTitle(filenameWithoutExtension(f.name));
    } else {
      setFile(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxFiles: 1,
    disabled: uploading,
  });

  const removeFile = () => {
    setFile(null);
    setTitle('');
    setFileError('');
  };

  const startExampleVideo = async (exampleVideoName) => {
    setLoadingExample(true);
    setFileError('');
    try {
      const recordedAt = new Date(recordedDate + 'T12:00:00').toISOString();
      const sessionTitle = title.trim() || 'Example session';
      const session = await createSession(sessionTitle, recordedAt);
      setExampleVideoPending({
        sessionId: session.id,
        exampleVideoName,
      });
      navigate(`/sessions/${session.id}/`);
    } catch (err) {
      const msg =
        err.response?.data?.error || 'Could not create session for example analysis.';
      toast.error(msg);
    } finally {
      setLoadingExample(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) {
      toast.error('Add a video and session title.');
      return;
    }
    const recordedAt = new Date(recordedDate + 'T12:00:00').toISOString();
    setUploading(true);
    try {
      const session = await createSession(title.trim(), recordedAt);
      const initData = await initDirectUpload(session.id, file);
      setPendingDirectUpload({
        sessionId: session.id,
        file,
        spec: initData.upload,
      });
      toast.success('Upload started');
      navigate(`/sessions/${session.id}/`);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <header className="px-4 pt-6 pb-2 flex items-center gap-3">
        <Video className="h-5 w-5 text-beachvision-coral" />
        <h1 className="font-display font-black text-xl tracking-tight text-beachvision-primary-light">
          Analysis
        </h1>
      </header>

      <div className="px-4 mt-8 space-y-6">
        {!file ? (
          <div
            {...getRootProps()}
            className={`rounded-2xl border-2 border-dashed border-beachvision-coral/70 p-10 text-center cursor-pointer transition-colors bg-beachvision-surface-low ${
              isDragActive ? 'bg-beachvision-elevated/80 border-beachvision-primary-light' : ''
            } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <input {...getInputProps()} />
            <Film className="h-12 w-12 text-beachvision-coral mx-auto mb-4" />
            <p className="text-white font-display font-black text-xl tracking-tight leading-snug">
              Upload match video for analysis
            </p>
            <p className="text-white font-display font-bold text-sm mt-4">Drag or tap to choose a video</p>
            <p className="text-beachvision-muted text-xs mt-2 leading-relaxed max-w-xs mx-auto">
              MP4, MOV or AVI · up to 500MB · 1080p recommended
            </p>
          </div>
        ) : (
          <div className="relative rounded-2xl border border-white/10 bg-beachvision-surface-low p-6 pr-14">
            <button
              type="button"
              onClick={removeFile}
              disabled={uploading}
              className="absolute top-4 right-4 rounded-lg p-2 text-beachvision-muted hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:pointer-events-none"
              aria-label="Remove video and start over"
            >
              <X className="h-5 w-5" />
            </button>
            <Film className="h-10 w-10 text-beachvision-coral mb-3" />
            <p className="text-white font-display font-bold text-lg leading-tight pr-2 break-words">
              {file.name}
            </p>
            <p className="text-beachvision-muted text-sm mt-2">{formatFileSizeMb(file.size)}</p>
          </div>
        )}

        {fileError ? (
          <p className="text-red-400 text-sm text-center">{fileError}</p>
        ) : null}

        {file ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="session-title" className="text-xs font-display font-bold uppercase text-beachvision-muted tracking-wide">
                Session title
              </label>
              <input
                id="session-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-2 w-full rounded-xl bg-transparent border-0 border-b-2 border-white/15 focus:border-beachvision-coral px-0 py-3 text-white placeholder-beachvision-muted focus:ring-0 outline-none transition-colors"
                placeholder="e.g. Morning power session"
                required
              />
            </div>
            <div>
              <label htmlFor="recorded-date" className="text-xs font-display font-bold uppercase text-beachvision-muted tracking-wide">
                Date recorded
              </label>
              <input
                id="recorded-date"
                type="date"
                value={recordedDate}
                onChange={(e) => setRecordedDate(e.target.value)}
                className="mt-2 w-full rounded-xl bg-beachvision-elevated/50 px-4 py-3 text-white border-0"
                required
              />
            </div>

            <button
              type="submit"
              disabled={uploading}
              className="w-full bg-beachvision-cta text-beachvision-on-coral rounded-xl px-6 py-3.5 font-display font-bold disabled:opacity-60"
            >
              {uploading ? 'Uploading…' : 'Upload & analyze'}
            </button>
          </form>
        ) : null}

        {!file ? (
          <>
            <div className="flex items-center gap-3 py-2">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-xs font-display font-bold text-beachvision-muted uppercase">or</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <button
              type="button"
              className="w-full rounded-2xl bg-beachvision-elevated py-4 flex items-center justify-center gap-2 text-white font-display font-bold disabled:opacity-60"
              disabled={uploading || loadingExample}
              onClick={() => setIsExampleModalOpen(true)}
            >
              <Video className="h-5 w-5 text-beachvision-coral" />
              {loadingExample ? 'Starting example analysis…' : 'Use example video'}
            </button>
          </>
        ) : null}

        <div className="space-y-3 pt-4">
          <p className="text-xs font-display font-bold uppercase text-beachvision-muted tracking-wide">
            What you get
          </p>
          {[
            {
              icon: Zap,
              title: 'Fast enough for real life',
              body: 'Most uploads are ready in a few minutes, so you can get coaching tips in between matches',
            },
            {
              icon: ListVideo,
              title: 'Rally-by-rally breakdown',
              body: 'Open each point on its own instead of scrubbing through the whole match.',
            },
            {
              icon: Activity,
              title: 'Clear coaching notes',
              body: 'See what worked, what to adjust, and the exact moments it happened.',
            },
            {
              icon: ShieldCheck,
              title: 'Securely saved to your account',
              body: 'Come back anytime to rewatch clips and review past sessions, or track your improvement',
            },
          ].map(({ icon: Icon, title: t, body }) => (
            <div key={t} className="rounded-2xl bg-beachvision-surface-low p-4 flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-beachvision-teal/15 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-beachvision-teal" />
              </div>
              <div>
                <p className="font-display font-bold text-white text-sm uppercase tracking-wide">
                  {t}
                </p>
                <p className="text-beachvision-muted text-sm mt-1 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isExampleModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Choose example video"
          onClick={() => setIsExampleModalOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-white/15 bg-beachvision-elevated/92 backdrop-blur-xl shadow-2xl shadow-black/40 ring-1 ring-white/10 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display font-black text-lg text-white">Pick a test clip</h3>
                <p className="text-xs text-beachvision-muted mt-1">
                  Select a bundled example to run analysis.
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-beachvision-muted hover:text-white"
                onClick={() => setIsExampleModalOpen(false)}
                aria-label="Close example video picker"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {EXAMPLE_VIDEOS.map((videoName) => (
                <button
                  key={videoName}
                  type="button"
                  className="w-full rounded-xl border border-white/10 bg-beachvision-surface-low p-2 flex items-center gap-3 hover:border-beachvision-coral/60 transition-colors"
                  disabled={loadingExample}
                  onClick={() => {
                    setIsExampleModalOpen(false);
                    startExampleVideo(videoName);
                  }}
                >
                  <LazyFrameImage
                    className="h-14 w-20 rounded-lg overflow-hidden shrink-0"
                    loadImage={() => getExampleVideoFrame(videoName)}
                    alt={`${videoName} preview`}
                  />
                  <p className="text-sm font-display font-bold text-white text-left truncate">
                    {videoName}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default function UploadPage() {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <UploadInner />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
